/* eslint-disable react/prop-types */
import { AlertTriangle, Plus, RotateCcw, Save, Send, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import { discardCmsDraft, fetchCmsContent, fetchCmsVersions, formatDateTime, publishCmsContent, rollbackCmsContent, saveCmsDraft } from '../lib/cms'
import {
  discardCampusGraphDraft, fetchCampusGraphContent, fetchCampusGraphVersions, publishCampusGraph,
  rollbackCampusGraph, saveCampusGraphDraft, validateCampusGraphDraft,
} from '../lib/campusGraphCms'
import { validateBuildingItem, validateCmsDraft, validateFacilityItem } from '../../../../packages/validation/cms'
import { EMPTY_BUILDING_ITEM, EMPTY_FACILITY_ITEM, FACILITY_CATEGORIES } from '../../../../packages/types/cms'
import { EMPTY_CAMPUS_GRAPH_DATA } from '../../../../packages/types/campusGraph'

const TABS = ['buildings', 'facilities', 'graph']
const TAB_LABELS = { buildings: '건물', facilities: '시설', graph: '보행 그래프' }

function newId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
}

// ── 건물/시설 공용: cmsContent(faq/homeConfig와 동일한 구조) 목록+에디터 ──────────────────

function CmsListEditor({ contentType, emptyItem, validateItem, renderFields, itemLabel, canEdit, uid }) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [items, setItems] = useState([])
  const [versions, setVersions] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [search, setSearch] = useState('')
  const savedSnapshotRef = useRef('[]')

  async function load() {
    setLoading(true); setError('')
    try {
      const { production: prod, draft: dr } = await fetchCmsContent(contentType)
      setProduction(prod); setDraft(dr)
      const initial = dr?.data?.items ?? prod?.data?.items ?? []
      setItems(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      setVersions(await fetchCmsVersions(contentType))
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- contentType 변경 시에만 다시 불러온다(FaqPage/ShuttlePage와 동일한 mount-load 패턴).
  useEffect(() => { load() }, [contentType])

  const dirty = JSON.stringify(items) !== savedSnapshotRef.current
  const validation = useMemo(() => validateCmsDraft(contentType, { items }), [contentType, items])
  const filtered = useMemo(() => items.filter(item => !search || `${item.name} ${item.id} ${item.category}`.toLowerCase().includes(search.toLowerCase())), [items, search])
  const selected = items.find(item => item.id === selectedId) || null

  function updateSelected(patch) { setItems(current => current.map(item => item.id === selectedId ? { ...item, ...patch } : item)) }
  function addItem() {
    const item = { ...emptyItem, id: newId(contentType) }
    setItems(current => [...current, item])
    setSelectedId(item.id)
  }
  function toggleActive(item) { setItems(current => current.map(entry => entry.id === item.id ? { ...entry, active: !(entry.active !== false) } : entry)) }

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCmsDraft(contentType, { items }, { uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify(items)
      const { draft: fresh } = await fetchCmsContent(contentType)
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCmsDraft(contentType, uid); await load(); setMessage('초안을 버렸습니다.') } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 배포할 수 없습니다.'); return }
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCmsContent(contentType, { items }, { uid })
      await load()
      setMessage(`v${version}으로 배포했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }
  async function rollback(version) {
    if (!window.confirm(`v${version}으로 롤백할까요?`)) return
    setBusy(true); setMessage(''); setError('')
    try { const next = await rollbackCmsContent(contentType, version, { uid }); await load(); setMessage(`v${version} → v${next}로 롤백했습니다.`) } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  if (loading) return <div className="empty-state">{itemLabel} 데이터를 불러오는 중…</div>
  return (
    <>
      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">production v{production?.version ?? 0}{draft ? ` · 초안 저장 ${formatDateTime(draft.savedAt)}` : ' · 초안 없음'}</span>
            {validation.errors.length > 0 && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
            {validation.warnings.length > 0 && <span className="status-badge new">경고 {validation.warnings.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 200 }} disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {validation.errors.length > 0 && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--danger)' }}>
            {validation.errors.slice(0, 8).map((entry, idx) => <li key={idx}>{entry.id ? `[${entry.id}] ` : ''}{entry.field}: {entry.message}</li>)}
          </ul>
        )}
      </section>
      {error && <div className="error-state" style={{ marginBottom: 16 }}>{error}</div>}
      {message && <div className="temporary-warning" style={{ marginBottom: 16 }}>{message}</div>}

      <section className={`panel split-view ${selected ? 'has-selection' : ''}`}>
        <div className="split-list">
          <div className="toolbar">
            <input className="input" placeholder={`${itemLabel} 검색`} value={search} onChange={e => setSearch(e.target.value)} />
            {canEdit && <button className="button secondary" type="button" onClick={addItem}><Plus size={16} />추가</button>}
          </div>
          <div className="split-list-items">
            {filtered.length === 0 ? <div className="empty-state">{itemLabel}이 없습니다.</div> : filtered.map(item => (
              <button type="button" key={item.id} className={`split-item ${item.id === selectedId ? 'active' : ''}`} onClick={() => setSelectedId(item.id)}>
                <div className="split-item-top">
                  <strong>{item.name || item.id}</strong>
                  <span className={`status-badge ${item.active === false ? 'ended' : 'published'}`}>{item.active === false ? '비활성' : '활성'}</span>
                </div>
                <p>{item.id} · {item.category} · {item.lat}, {item.lng}</p>
              </button>
            ))}
          </div>
        </div>
        <article className="detail-panel">
          {!selected ? <div className="empty-state">왼쪽 목록에서 {itemLabel}을 선택하거나 새로 만드세요.</div> : (
            <div className="form-section">
              <h2>{itemLabel} 정보</h2>
              {renderFields(selected, updateSelected, canEdit)}
              {canEdit && <button type="button" className="button secondary" style={{ marginTop: 12 }} onClick={() => toggleActive(selected)}><Trash2 size={14} />{selected.active === false ? '활성화' : '비활성화'}</button>}
              {!validateItem(selected).valid && (
                <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--danger)' }}>
                  {Object.entries(validateItem(selected).errors).map(([field, msg]) => <li key={field}>{field}: {msg}</li>)}
                </ul>
              )}
            </div>
          )}
        </article>
      </section>
    </>
  )
}

function BuildingFields(item, set, canEdit) {
  return (
    <div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>이름</label><input className="input" disabled={!canEdit} value={item.name} onChange={e => set({ name: e.target.value })} /></div>
      <div className="field"><label>카테고리</label><input className="input" disabled={!canEdit} value={item.category} onChange={e => set({ category: e.target.value })} /></div>
      <div className="field"><label>상태</label>
        <select className="select" disabled={!canEdit} value={item.status ?? 'confirmed'} onChange={e => set({ status: e.target.value })}>
          <option value="confirmed">confirmed</option><option value="candidate">candidate</option><option value="estimated">estimated</option><option value="manual_required">manual_required</option>
        </select>
      </div>
      <div className="field"><label>위도(lat)</label><input className="input" disabled={!canEdit} value={item.lat} onChange={e => set({ lat: e.target.value })} /></div>
      <div className="field"><label>경도(lng)</label><input className="input" disabled={!canEdit} value={item.lng} onChange={e => set({ lng: e.target.value })} /></div>
      <div className="field full"><label>설명</label><input className="input" disabled={!canEdit} value={item.description ?? ''} onChange={e => set({ description: e.target.value })} /></div>
      <div className="field"><label>전화번호</label><input className="input" disabled={!canEdit} value={item.phone ?? ''} onChange={e => set({ phone: e.target.value })} /></div>
      <div className="field full"><label>사진 URL (https://)</label><input className="input" disabled={!canEdit} value={item.photoUrl ?? ''} onChange={e => set({ photoUrl: e.target.value })} placeholder="https://…" /></div>
      <div className="field"><label>입구 위도</label><input className="input" disabled={!canEdit} value={item.entranceLat ?? ''} onChange={e => set({ entranceLat: e.target.value })} /></div>
      <div className="field"><label>입구 경도</label><input className="input" disabled={!canEdit} value={item.entranceLng ?? ''} onChange={e => set({ entranceLng: e.target.value })} /></div>
      <div className="field"><label>입구 이름</label><input className="input" disabled={!canEdit} value={item.entranceLabel ?? ''} onChange={e => set({ entranceLabel: e.target.value })} /></div>
      <div className="field"><label>입구 검수 상태</label>
        <select className="select" disabled={!canEdit} value={item.entranceStatus ?? 'pending'} onChange={e => set({ entranceStatus: e.target.value })}>
          <option value="pending">pending</option><option value="checked">checked</option><option value="fixed">fixed</option>
        </select>
      </div>
    </div>
  )
}

function FacilityFields(item, set, canEdit) {
  return (
    <div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>이름</label><input className="input" disabled={!canEdit} value={item.name} onChange={e => set({ name: e.target.value })} /></div>
      <div className="field"><label>카테고리</label>
        <select className="select" disabled={!canEdit} value={item.category} onChange={e => set({ category: e.target.value })}>
          {FACILITY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="field"><label>상태</label><input className="input" disabled={!canEdit} value={item.status ?? ''} onChange={e => set({ status: e.target.value })} /></div>
      <div className="field"><label>위도(lat)</label><input className="input" disabled={!canEdit} value={item.lat} onChange={e => set({ lat: e.target.value })} /></div>
      <div className="field"><label>경도(lng)</label><input className="input" disabled={!canEdit} value={item.lng} onChange={e => set({ lng: e.target.value })} /></div>
      <div className="field full"><label>설명</label><input className="input" disabled={!canEdit} value={item.description ?? ''} onChange={e => set({ description: e.target.value })} /></div>
      <div className="field full"><label>사진 URL (https://)</label><input className="input" disabled={!canEdit} value={item.photoUrl ?? ''} onChange={e => set({ photoUrl: e.target.value })} placeholder="https://…" /></div>
    </div>
  )
}

// ── 보행 그래프: 청크 배포(노드/엣지 표 편집) ──────────────────────────────────────

function CampusGraphSection({ canEdit, uid }) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [data, setData] = useState(EMPTY_CAMPUS_GRAPH_DATA)
  const [versions, setVersions] = useState([])
  const [section, setSection] = useState('nodes')
  const savedSnapshotRef = useRef('')

  async function load() {
    setLoading(true); setError('')
    try {
      const { draft: dr, draftManifest, production: prod, productionManifest } = await fetchCampusGraphContent()
      setDraft(draftManifest); setProduction(productionManifest)
      const initial = dr ?? prod ?? EMPTY_CAMPUS_GRAPH_DATA
      setData(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      setVersions(await fetchCampusGraphVersions())
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const dirty = JSON.stringify(data) !== savedSnapshotRef.current
  const validation = useMemo(() => validateCampusGraphDraft(data), [data])
  const nodeIds = new Set((data.nodes ?? []).map(n => n.id))

  function addNode() { setData(current => ({ ...current, nodes: [...(current.nodes ?? []), { id: newId('n'), label: '', lat: '', lng: '' }] })) }
  function updateNode(index, patch) { setData(current => ({ ...current, nodes: current.nodes.map((n, i) => i === index ? { ...n, ...patch } : n) })) }
  function removeNode(index) { setData(current => ({ ...current, nodes: current.nodes.filter((_, i) => i !== index) })) }
  function addEdge() { setData(current => ({ ...current, edges: [...(current.edges ?? []), { id: newId('e'), from: '', to: '' }] })) }
  function updateEdge(index, patch) { setData(current => ({ ...current, edges: current.edges.map((e, i) => i === index ? { ...e, ...patch } : e) })) }
  function removeEdge(index) { setData(current => ({ ...current, edges: current.edges.filter((_, i) => i !== index) })) }

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCampusGraphDraft(data, { uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify(data)
      const { draftManifest } = await fetchCampusGraphContent()
      setDraft(draftManifest)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCampusGraphDraft(uid); await load(); setMessage('초안을 버렸습니다.') } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 배포할 수 없습니다.'); return }
    if (!window.confirm('새 그래프를 배포할까요? 청크를 저장하고 검증한 뒤 production 포인터를 전환합니다.')) return
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCampusGraph(data, { uid })
      await load()
      setMessage(`v${version}으로 배포했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }
  async function rollback(version) {
    if (!window.confirm(`v${version}으로 production 포인터를 되돌릴까요? (새 버전을 만들지 않고 기존 청크를 재사용합니다)`)) return
    setBusy(true); setMessage(''); setError('')
    try { await rollbackCampusGraph(version, { uid }); await load(); setMessage(`v${version}로 롤백했습니다.`) } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  if (loading) return <div className="empty-state">보행 그래프를 불러오는 중…</div>
  return (
    <>
      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">production v{production?.version ?? '없음'}{draft ? ` · 초안 저장 ${formatDateTime(draft.savedAt)}` : ' · 초안 없음'}</span>
            <span className="muted">노드 {data.nodes?.length ?? 0} · 엣지 {data.edges?.length ?? 0}</span>
            {validation.errors.length > 0 && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
            {validation.warnings.length > 0 && <span className="status-badge new">경고 {validation.warnings.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 220 }} disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {(validation.errors.length > 0 || validation.warnings.length > 0) && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {validation.errors.slice(0, 10).map((e, idx) => <li key={`e${idx}`} style={{ color: 'var(--danger)' }}>{e.id ? `[${e.id}] ` : ''}{e.field}: {e.message}</li>)}
            {validation.warnings.slice(0, 5).map((w, idx) => <li key={`w${idx}`} className="muted">{w.message}</li>)}
          </ul>
        )}
      </section>
      {error && <div className="error-state" style={{ marginBottom: 16 }}>{error}</div>}
      {message && <div className="temporary-warning" style={{ marginBottom: 16 }}>{message}</div>}

      <div className="toolbar">
        <button type="button" className={`check-chip ${section === 'nodes' ? 'active' : ''}`} onClick={() => setSection('nodes')}>노드 ({data.nodes?.length ?? 0})</button>
        <button type="button" className={`check-chip ${section === 'edges' ? 'active' : ''}`} onClick={() => setSection('edges')}>엣지 ({data.edges?.length ?? 0})</button>
      </div>

      <section className="panel table-wrap">
        {section === 'nodes' ? (
          <table className="data-table"><thead><tr><th>id</th><th>label</th><th>lat</th><th>lng</th><th /></tr></thead><tbody>
            {(data.nodes ?? []).map((node, index) => (
              <tr key={index}>
                <td><input className="input" disabled={!canEdit} value={node.id} onChange={e => updateNode(index, { id: e.target.value })} /></td>
                <td><input className="input" disabled={!canEdit} value={node.label ?? ''} onChange={e => updateNode(index, { label: e.target.value })} /></td>
                <td><input className="input" disabled={!canEdit} value={node.lat} onChange={e => updateNode(index, { lat: e.target.value })} /></td>
                <td><input className="input" disabled={!canEdit} value={node.lng} onChange={e => updateNode(index, { lng: e.target.value })} /></td>
                <td>{canEdit && <button type="button" className="icon-button" onClick={() => removeNode(index)}><Trash2 size={14} /></button>}</td>
              </tr>
            ))}
          </tbody></table>
        ) : (
          <table className="data-table"><thead><tr><th>id</th><th>from</th><th>to</th><th /></tr></thead><tbody>
            {(data.edges ?? []).map((edge, index) => (
              <tr key={index}>
                <td><input className="input" disabled={!canEdit} value={edge.id} onChange={e => updateEdge(index, { id: e.target.value })} /></td>
                <td>
                  <select className="select" disabled={!canEdit} value={edge.from} onChange={e => updateEdge(index, { from: e.target.value })}>
                    <option value="">선택…</option>{[...nodeIds].map(id => <option key={id} value={id}>{id}</option>)}
                  </select>
                </td>
                <td>
                  <select className="select" disabled={!canEdit} value={edge.to} onChange={e => updateEdge(index, { to: e.target.value })}>
                    <option value="">선택…</option>{[...nodeIds].map(id => <option key={id} value={id}>{id}</option>)}
                  </select>
                </td>
                <td>{canEdit && <button type="button" className="icon-button" onClick={() => removeEdge(index)}><Trash2 size={14} /></button>}</td>
              </tr>
            ))}
          </tbody></table>
        )}
        {canEdit && (
          <div style={{ padding: 12 }}>
            <button type="button" className="button secondary" onClick={section === 'nodes' ? addNode : addEdge}><Plus size={14} />{section === 'nodes' ? '노드' : '엣지'} 추가</button>
          </div>
        )}
      </section>
    </>
  )
}

export default function MapPage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [tab, setTab] = useState('buildings')

  return (
    <>
      <header className="page-header"><div><p className="eyebrow">서비스</p><h1>캠퍼스 지도</h1><p>건물·시설·보행 경로를 관리합니다. 배포한 내용은 앱에 반영됩니다.</p></div></header>
      <div className="toolbar">
        {TABS.map(t => <button key={t} type="button" className={`check-chip ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{TAB_LABELS[t]}</button>)}
      </div>
      {tab === 'buildings' && <CmsListEditor contentType="buildings" emptyItem={EMPTY_BUILDING_ITEM} validateItem={validateBuildingItem} renderFields={BuildingFields} itemLabel="건물" canEdit={canEdit} uid={auth.user.uid} />}
      {tab === 'facilities' && <CmsListEditor contentType="facilities" emptyItem={EMPTY_FACILITY_ITEM} validateItem={validateFacilityItem} renderFields={FacilityFields} itemLabel="시설" canEdit={canEdit} uid={auth.user.uid} />}
      {tab === 'graph' && <CampusGraphSection canEdit={canEdit} uid={auth.user.uid} />}
    </>
  )
}
